/**
 * Reporte descargable por socio comercial (partner) de Splits > Proyección — CP-334.
 *
 * Funciones PURAS (sin DOM) para armar el reporte a partir de la respuesta de
 * GET /splits/projection, más un helper de descarga. Se testean con
 * `npm test` (node --test, sin dependencias).
 *
 * Formato elegido (ver docs/cp-334-splits-reportes.md):
 *  - CSV UTF-8 con BOM, separador `;`, decimales con coma, fin de línea CRLF →
 *    Excel en español lo abre con doble click sin asistente de importación.
 *  - Montos con 2 decimales fijos, sin separador de miles. Tasa en % (0.008 → "0,8").
 *  - Un archivo por socio: `split-<socio>-<desde>[-<hasta>].csv`.
 */
import type { SplitProjection, SplitProjectionDetailRow, SplitProjectionRow } from "../services/splits";

export const CSV_SEPARATOR = ";";
export const CSV_EOL = "\r\n";
export const CSV_BOM = "\uFEFF";

export const DETAIL_HEADERS = [
  "Mes", "Colector", "Adquirente", "Moneda",
  "Cobros (cant.)", "Cobros (monto)", "Rechazados (cant.)", "Rechazados (monto)",
  "Tasa (%)", "Split estimado", "Mes parcial",
] as const;

export const SUMMARY_HEADERS = ["Colector", "Tasa (%)", "Cobros (monto)", "Split estimado"] as const;

export interface PartnerReportTotal {
  currency:        string;
  count:           number;
  amount:          number;
  count_rejected:  number;
  amount_rejected: number;
  projected:       number;
}

export interface PartnerReport {
  partner:     string;
  months:      string[];                 // yyyyMM, en orden
  generatedAt: string;
  /** Filas colector × mes (× adquirente si hubo cobros en más de uno). Incluye filas
   *  en cero para los meses sin cobros, así el socio ve el período completo.
   *  `null` si el backend no devolvió `detail` (versión anterior al CP-334). */
  detail:      SplitProjectionDetailRow[] | null;
  /** Totales del detalle, uno por moneda. */
  totals:      PartnerReportTotal[];
  /** Resumen por colector del rango completo (lo mismo que la tabla en pantalla). */
  collectors:  SplitProjectionRow[];
  collectorsTotal: { cobros: number; projected: number };
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const byName = (a: string, b: string) => a.localeCompare(b, "es", { sensitivity: "base" });

/** Arma el reporte de UN socio a partir de la proyección completa. */
export function buildPartnerReport(projection: SplitProjection, partner: string): PartnerReport {
  const collectors = projection.rows
    .filter((r) => r.partner === partner)
    .slice()
    .sort((a, b) => byName(a.collector, b.collector));

  let detail: SplitProjectionDetailRow[] | null = null;
  const totalsByCurrency = new Map<string, PartnerReportTotal>();

  if (Array.isArray(projection.detail)) {
    const real = projection.detail.filter((d) => d.partner === partner);
    const currencyOfCollector = new Map<string, string>();
    const currencyCount = new Map<string, number>();
    for (const d of real) {
      if (!d.currency) continue;
      if (!currencyOfCollector.has(d.collector)) currencyOfCollector.set(d.collector, d.currency);
      currencyCount.set(d.currency, (currencyCount.get(d.currency) ?? 0) + 1);
    }
    const dominant = [...currencyCount.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";

    detail = [];
    for (const c of collectors) {
      for (const month of projection.months) {
        const rows = real
          .filter((d) => d.collector === c.collector && d.month === month)
          .sort((a, b) => byName(a.acquirer, b.acquirer));
        if (rows.length) {
          detail.push(...rows);
        } else {
          detail.push({
            collector: c.collector, partner, rate: c.rate, month, acquirer: "",
            currency: currencyOfCollector.get(c.collector) ?? dominant,
            count: 0, amount: 0, count_rejected: 0, amount_rejected: 0, projected: 0, partial: false,
          });
        }
      }
    }
    for (const d of detail) {
      const t = totalsByCurrency.get(d.currency) ?? {
        currency: d.currency, count: 0, amount: 0, count_rejected: 0, amount_rejected: 0, projected: 0,
      };
      t.count += d.count;
      t.amount += d.amount;
      t.count_rejected += d.count_rejected;
      t.amount_rejected += d.amount_rejected;
      t.projected += d.projected;
      totalsByCurrency.set(d.currency, t);
    }
  }

  let totals = [...totalsByCurrency.values()].map((t) => ({
    ...t, amount: round2(t.amount), amount_rejected: round2(t.amount_rejected), projected: round2(t.projected),
  }));
  // Si hay varias monedas, las filas en cero sin moneda conocida no suman nada: fuera.
  if (totals.length > 1) totals = totals.filter((t) => t.count || t.amount || t.currency);
  totals.sort((a, b) => byName(a.currency, b.currency));

  return {
    partner,
    months: projection.months,
    generatedAt: projection.generated_at,
    detail,
    totals,
    collectors,
    collectorsTotal: {
      cobros:    round2(collectors.reduce((s, r) => s + r.cobros, 0)),
      projected: round2(collectors.reduce((s, r) => s + r.projected, 0)),
    },
  };
}

/* ------------------------------ formato CSV ------------------------------ */

/** 2 decimales fijos, coma decimal, sin miles: 3500.5 → "3500,50". */
export const fmtAmount = (n: number) => n.toFixed(2).replace(".", ",");

/** Tasa (fracción) → porcentaje con hasta 6 decimales, sin ceros de cola: 0.00847 → "0,847". */
export function fmtRatePct(rate: number): string {
  const s = (rate * 100).toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
  return s.replace(".", ",");
}

/** yyyyMM → yyyy-MM (lo que muestra el selector de mes en pantalla). */
export const fmtMonth = (m: string) => (/^\d{6}$/.test(m) ? `${m.slice(0, 4)}-${m.slice(4)}` : m);

export function csvEscape(v: string | number | boolean | null | undefined): string {
  if (v == null) return "";
  const s = String(v);
  return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const line = (cells: ReadonlyArray<string | number | boolean | null | undefined>) =>
  cells.map(csvEscape).join(CSV_SEPARATOR);

export function periodLabel(months: string[]): string {
  if (!months.length) return "";
  const a = fmtMonth(months[0]), b = fmtMonth(months[months.length - 1]);
  return a === b ? a : `${a} a ${b}`;
}

/** Serializa el reporte a CSV (con BOM, listo para descargar). */
export function partnerReportToCsv(report: PartnerReport): string {
  const out: string[] = [];
  out.push(line(["Reporte de split", report.partner]));
  out.push(line(["Período", periodLabel(report.months)]));
  out.push(line(["Generado", report.generatedAt]));
  out.push(line(["Nota", "Estimación: cobros × tasa por mes. El split no se liquida automáticamente. "
    + "Montos brutos (incluyen rechazados, informados aparte), en la moneda de cada fila."]));
  out.push("");

  if (report.detail) {
    out.push("Detalle mensual");
    out.push(line(DETAIL_HEADERS));
    for (const d of report.detail) {
      out.push(line([
        fmtMonth(d.month), d.collector, d.acquirer, d.currency,
        d.count, fmtAmount(d.amount), d.count_rejected, fmtAmount(d.amount_rejected),
        fmtRatePct(d.rate), fmtAmount(d.projected), d.partial ? "Sí" : "No",
      ]));
    }
    for (const t of report.totals) {
      out.push(line([
        `Total ${t.currency}`.trim(), "", "", t.currency,
        t.count, fmtAmount(t.amount), t.count_rejected, fmtAmount(t.amount_rejected),
        "", fmtAmount(t.projected), "",
      ]));
    }
    out.push("");
  }

  out.push("Resumen por colector (rango completo)");
  out.push(line(SUMMARY_HEADERS));
  for (const c of report.collectors) {
    out.push(line([c.collector, fmtRatePct(c.rate), fmtAmount(c.cobros), fmtAmount(c.projected)]));
  }
  out.push(line(["Total", "", fmtAmount(report.collectorsTotal.cobros), fmtAmount(report.collectorsTotal.projected)]));

  return CSV_BOM + out.join(CSV_EOL) + CSV_EOL;
}

/** `split-<socio>-<desde>[-<hasta>].csv` — solo [A-Za-z0-9_-] en el nombre del socio. */
export function partnerReportFilename(partner: string, months: string[]): string {
  const safe = partner.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "socio";
  const first = months[0] ?? "", last = months[months.length - 1] ?? "";
  const period = !first ? "sin-periodo" : first === last ? first : `${first}-${last}`;
  return `split-${safe}-${period}.csv`;
}

/* -------------------------------- descarga -------------------------------- */

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadPartnerReport(projection: SplitProjection, partner: string): void {
  const report = buildPartnerReport(projection, partner);
  downloadCsv(partnerReportFilename(partner, projection.months), partnerReportToCsv(report));
}

/** Un archivo por socio, con una pausa corta entre descargas (los browsers
 *  descartan descargas disparadas en el mismo tick). */
export async function downloadAllPartnerReports(projection: SplitProjection): Promise<number> {
  const partners = projection.by_partner.map((p) => p.partner);
  for (const [i, partner] of partners.entries()) {
    if (i > 0) await new Promise((r) => setTimeout(r, 300));
    downloadPartnerReport(projection, partner);
  }
  return partners.length;
}
